import { Alert, Skeleton } from "@hakistudio/hakiui";
import { formatAmount, STOCK_STATUS_LABELS, type StockStatus } from "@sebas/core";
import { Check, ChefHat, Pencil, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Field";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/cn";
import {
  useAddIngredient,
  useCookRecipe,
  useDeleteIngredient,
  useIngredients,
  useUpdateIngredient,
  type CookResult,
  type Ingredient,
} from "@/lib/pantry";
import { parseQuantity, QuantityFields, type AmountDraft } from "./UnitFields";

type Draft = AmountDraft & { name: string; note: string };

const emptyDraft: Draft = { name: "", quantity: "", unit: "", note: "" };

const draftOf = (i: Ingredient): Draft => ({
  name: i.name,
  quantity: i.quantity === null ? "" : String(i.quantity),
  unit: i.unit,
  note: i.note ?? "",
});

const toPayload = (draft: Draft) => ({
  name: draft.name.trim(),
  quantity: parseQuantity(draft.quantity),
  unit: draft.unit,
  note: draft.note.trim() || null,
});

/**
 * Status colours reuse the tile palette, which stays saturated in both themes
 * and always takes dark ink — the same rule the note tiles follow.
 */
const STATUS_STYLE: Record<StockStatus, string> = {
  have: "bg-tile-mint text-tile-ink",
  unknown: "bg-sunken text-ink-muted",
  partial: "bg-tile-amber text-tile-ink",
  missing: "bg-tile-coral text-tile-ink",
};

function StatusPill({ status }: { status: StockStatus }) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-2.5 py-0.5 text-[0.6875rem] font-semibold",
        STATUS_STYLE[status],
      )}
    >
      {STOCK_STATUS_LABELS[status]}
    </span>
  );
}

/** What the fridge holds for this ingredient, when it is worth spelling out. */
function stockLine(i: Ingredient): string | null {
  if (!i.inFridge) return null;
  const amount = formatAmount({ quantity: i.inFridge.quantity, unit: i.inFridge.unit });
  return amount ? `${amount} in the fridge` : "in the fridge, amount unknown";
}

export function IngredientsPanel({ noteId, canEdit }: { noteId: string; canEdit: boolean }) {
  const { data, isPending } = useIngredients(noteId);
  const add = useAddIngredient(noteId);
  const update = useUpdateIngredient(noteId);
  const remove = useDeleteIngredient(noteId);
  const cook = useCookRecipe(noteId);

  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [editing, setEditing] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);
  const [cooked, setCooked] = useState<CookResult["summary"] | null>(null);

  const fail = (e: unknown) => {
    setCooked(null);
    setError(e instanceof ApiError ? e.message : String(e));
  };

  const submitNew = (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim()) return;
    setError(null);
    add.mutate(toPayload(draft), { onSuccess: () => setDraft(emptyDraft), onError: fail });
  };

  const saveEdit = (id: string) => {
    if (!editDraft.name.trim()) return;
    update.mutate({ id, ...toPayload(editDraft) }, { onSuccess: () => setEditing(null), onError: fail });
  };

  const runCook = (force: boolean) => {
    setError(null);
    setCooked(null);
    cook.mutate(force, { onSuccess: (r) => setCooked(r.summary), onError: fail });
  };

  const ingredients = data?.ingredients ?? [];
  const summary = data?.summary;
  const short = ingredients.filter((i) => i.status === "missing" || i.status === "partial");

  return (
    <section className="flex flex-col gap-3 rounded-card border border-line bg-raised p-5">
      <header className="flex flex-wrap items-baseline gap-3">
        <h2 className="font-display text-xl font-semibold text-ink">Ingredients</h2>
        {summary && summary.total > 0 ? (
          <span className="text-xs text-ink-muted">
            {summary.ready === summary.total
              ? "You have everything"
              : `${summary.ready} of ${summary.total} on hand`}
          </span>
        ) : null}
        {canEdit && ingredients.length > 0 ? (
          <Button
            className="ml-auto"
            size="sm"
            disabled={cook.isPending}
            onClick={() => runCook(false)}
          >
            <ChefHat className="h-4 w-4" /> Cook this
          </Button>
        ) : null}
      </header>

      {canEdit ? (
        <form onSubmit={submitNew} className="flex flex-wrap items-center gap-2">
          <Input
            className="min-w-40 flex-1"
            size="sm"
            placeholder="Add an ingredient"
            aria-label="Ingredient name"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <QuantityFields draft={draft} onChange={(a) => setDraft({ ...draft, ...a })} />
          <Input
            className="w-40 shrink-0"
            size="sm"
            placeholder="finely chopped"
            aria-label="Preparation note"
            value={draft.note}
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          />
          <Button type="submit" size="sm" disabled={add.isPending || !draft.name.trim()}>
            <Plus className="h-4 w-4" /> Add
          </Button>
        </form>
      ) : null}

      {error ? (
        <Alert variant="danger" title={error}>
          {short.length > 0 && canEdit ? (
            <span className="flex items-center gap-3">
              Cook anyway and take out only what is there?
              <button className="font-semibold underline" onClick={() => runCook(true)}>
                Cook anyway
              </button>
            </span>
          ) : null}
        </Alert>
      ) : null}

      {cooked ? (
        <Alert variant="success" title="Taken out of the fridge" onClose={() => setCooked(null)}>
          <span className="flex flex-col gap-1">
            {cooked.deducted.length > 0 ? <span>Used: {cooked.deducted.join(", ")}.</span> : null}
            {cooked.usedUp.length > 0 ? (
              <span>Ran out of: {cooked.usedUp.join(", ")}.</span>
            ) : null}
            {cooked.skipped.length > 0 ? (
              <span>Left alone (amounts do not compare): {cooked.skipped.join(", ")}.</span>
            ) : null}
          </span>
        </Alert>
      ) : null}

      {isPending ? (
        <Skeleton height="4rem" radius="lg" />
      ) : ingredients.length === 0 ? (
        <p className="text-sm text-ink-faint">
          Add what this recipe needs and each line will be checked against your fridge.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {ingredients.map((i) =>
            editing === i.id ? (
              <li
                key={i.id}
                className="flex flex-wrap items-center gap-2 rounded-2xl bg-sunken px-3 py-2"
              >
                <Input
                  className="min-w-32 flex-1"
                  size="sm"
                  aria-label="Ingredient name"
                  value={editDraft.name}
                  onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                />
                <QuantityFields
                  draft={editDraft}
                  onChange={(a) => setEditDraft({ ...editDraft, ...a })}
                />
                <Input
                  className="w-40 shrink-0"
                  size="sm"
                  placeholder="finely chopped"
                  aria-label="Preparation note"
                  value={editDraft.note}
                  onChange={(e) => setEditDraft({ ...editDraft, note: e.target.value })}
                />
                <Button size="icon" aria-label="Save" onClick={() => saveEdit(i.id)}>
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Cancel"
                  onClick={() => setEditing(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </li>
            ) : (
              <li key={i.id} className="flex items-center gap-3 rounded-2xl bg-sunken px-3 py-2">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink">
                    {formatAmount({ quantity: i.quantity, unit: i.unit })} {i.name}
                    {i.note ? <span className="text-ink-faint"> — {i.note}</span> : null}
                  </span>
                  {stockLine(i) ? (
                    <span className="text-xs text-ink-faint">{stockLine(i)}</span>
                  ) : null}
                </span>

                <StatusPill status={i.status} />

                {canEdit ? (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Edit ${i.name}`}
                      onClick={() => {
                        setEditing(i.id);
                        setEditDraft(draftOf(i));
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${i.name}`}
                      onClick={() => remove.mutate(i.id, { onError: fail })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                ) : null}
              </li>
            ),
          )}
        </ul>
      )}
    </section>
  );
}
